/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	LitElement,
	html,
	CSSResultArray,
	TemplateResult
  } from 'lit';
  import { customElement, property } from 'lit/decorators.js';
  import { styles } from './nile-side-bar-action-menu-item.css';
  import '../nile-icon';
  import { classMap } from 'lit/directives/class-map.js';
  import { query } from 'lit/decorators.js';
  import { getTextContent } from '../internal/slot';
  import { watch } from '../internal/watch';
  import NileElement from '../internal/nile-element';
  import type { CSSResultGroup } from 'lit';
  
  /**
   * Nile side-bar-action-menu-item component.
   *
   * @tag nile-side-bar-action-menu-item
   *
   * @summary Menu items provide options for the user to pick from in a menu.
   * @status stable
   * @since 2.0
   *
   * @dependency nile-icon
   *
   * @slot - The menu item's label.
   * @slot prefix - Used to prepend an icon or similar element to the menu item.
   * @slot suffix - Used to append an icon or similar element to the menu item.
   *
   * @csspart base - The component's base wrapper.
   * @csspart checked-icon - The checked icon, which is only visible when the menu item is checked.
   * @csspart prefix - The prefix container.
   * @csspart label - The menu item label.
   * @csspart suffix - The suffix container.
   * @csspart submenu-icon - The submenu icon, visible only when the menu item has a submenu.
   */
  @customElement('nile-side-bar-action-menu-item')
  export class NileSideBarActionMenuItem extends NileElement {
	static styles: CSSResultGroup = styles;
  
	private cachedTextLabel: string;
  
	@query('slot:not([name])') defaultSlot: HTMLSlotElement;
  
	@query('.menu-item') menuItem: HTMLElement;
  
	/** The type of menu item to render. To use `checked`, this value must be set to `checkbox`. */
	@property() type: 'normal' | 'checkbox' = 'normal';
  
	/** Draws the item in a checked state. */
	@property({ type: Boolean, reflect: true }) checked = false;
  
	/** A unique value to store in the menu item. This can be used as a way to identify menu items when selected. */
	@property() value = '';
  
	/** Draws the menu item in a disabled state, preventing selection. */
	@property({ type: Boolean, reflect: true }) disabled = false;
  
	/** Indicates if this menu item has a submenu. */
	@property({ type: Boolean, reflect: true }) hasSubMenu = false;
  
	connectedCallback() {
	  super.connectedCallback();
	  this.handleHostClick = this.handleHostClick.bind(this);
	  this.addEventListener('click', this.handleHostClick);
	}
  
	disconnectedCallback() {
	  super.disconnectedCallback();
	  this.removeEventListener('click', this.handleHostClick);
	}
  
	private handleDefaultSlotChange() {
	  const textLabel = this.getTextLabel();
  
	  // Ignore the first time the label is set
	  if (typeof this.cachedTextLabel === 'undefined') {
		this.cachedTextLabel = textLabel;
		return;
	  }
  
	  // When the label changes, emit a slotchange event so parent controls see it
	  if (textLabel !== this.cachedTextLabel) {
		this.cachedTextLabel = textLabel;
		this.emit('slotchange');
	  }
	}
  
	private handleHostClick(event: MouseEvent) {
	  // Prevent the click event from being emitted when the item is disabled
	  if (this.disabled) {
		event.preventDefault();
		event.stopImmediatePropagation();
	  }
	}
  
	@watch('checked')
	handleCheckedChange() {
	  // For proper accessibility, users have to use type="checkbox" to use the checked attribute
	  if (this.checked && this.type !== 'checkbox') {
		this.checked = false;
		console.error(
		  'The checked attribute can only be used on menu items with type="checkbox"',
		  this
		);
		return;
	  }
  
	  // Only checkbox types can receive the aria-checked attribute
	  if (this.type === 'checkbox') {
		this.setAttribute('aria-checked', this.checked ? 'true' : 'false');
	  } else {
		this.removeAttribute('aria-checked');
	  }
	}
  
	@watch('disabled')
	handleDisabledChange() {
	  this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
	}
  
	@watch('type')
	handleTypeChange() {
	  if (this.type === 'checkbox') {
		this.setAttribute('role', 'menuitemcheckbox');
		this.setAttribute('aria-checked', this.checked ? 'true' : 'false');
	  } else {
		this.setAttribute('role', 'menuitem');
		this.removeAttribute('aria-checked');
	  }
	}
  
	/** Returns a text label based on the contents of the menu item's default slot. */
	getTextLabel() {
	  return getTextContent(this.defaultSlot);
	}
  
	render() {
	  return html`
		<div
		  part="base"
		  class=${classMap({
			'menu-item': true,
			'menu-item--checked': this.checked,
			'menu-item--disabled': this.disabled,
			'menu-item--has-submenu': this.hasSubMenu, // reserved for future use
		  })}
		>
		  ${this.checked
			? html`
				<span part="checked-icon" class="menu-item__check">
				  <nile-icon
					name="var(--nile-icon-tick, var(--ng-icon-check))"
					method="var(--nile-svg-method-fill, var(--ng-svg-method-stroke))"
					library="system"
					color="green"
					aria-hidden="true"
				  ></nile-icon>
				</span>
			  `
			: ``}
  
		  <slot name="prefix" part="prefix" class="menu-item__prefix"></slot>
  
		  <slot
			part="label"
			class="menu-item__label"
			@slotchange=${this.handleDefaultSlotChange}
		  ></slot>
  
		  <slot name="suffix" part="suffix" class="menu-item__suffix"></slot>
  
		  ${this.hasSubMenu
			? html` <span part="submenu-icon" class="menu-item__chevron">
				<nile-icon
				  name="var(--nile-icon-arrow-right, var(--ng-icon-chevron-right))"
				  method="var(--nile-svg-method-fill, var(--ng-svg-method-stroke))"
				  library="system"
				  aria-hidden="true"
				></nile-icon>
			  </span>`
			: ``}
		</div>
	  `;
	}
  }
  
  export default NileSideBarActionMenuItem;
  
  declare global {
	interface HTMLElementTagNameMap {
	  'nile-side-bar-action-menu-item': NileSideBarActionMenuItem;
	}
  }
  